import styles from './HomePage.module.scss';

import { Box, Grid, Stack, Theme, useMediaQuery } from '@mui/material';
import clsx from 'clsx';
import { cloneDeep } from 'lodash';
import { DateTime } from 'luxon';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Layout } from 'react-grid-layout';
import { useTranslation } from 'react-i18next';

import { FixedBackground } from '@work4all/components/lib/components/FixedBackground';
import { DEFAULT_CARD_HEADER_HEIGHT_REM } from '@work4all/components/lib/dataDisplay/card-widget/components/card-header/CardHeader';
import {
  DEFAULT_GRID_GAP_REM,
  DEFAULT_GRID_ROW_HEIGHT_REM,
  GridLayout,
} from '@work4all/components/lib/dataDisplay/grid-layout/GridLayout';
import { YSplit } from '@work4all/components/lib/layout/y-split/YSplit';
import { NavigationOverlay } from '@work4all/components/lib/navigation/navigation-overlay';
import { isTimeTrackingUser } from '@work4all/components/lib/utils/isTimeTrackingUser';

import {
  AppParts,
  DEFAULT_WIDGET_QUERY_SIZE,
  useCanView,
  useCustomBackgroundsContext,
  useModuleRights,
  useUser,
} from '@work4all/data';

import { User } from '@work4all/models/lib/Classes/User.entity';

import { UserButtonMobile } from '../../components/user-menu/components/UserButton/UserButtonMobile';
import { CalendarEventClickData } from '../../components/vacation/VacationOverview';
import { useSuggestionsContext } from '../../contexts/SuggestionsContext';
import { useSetting } from '../../settings';
import {
  HomeWidget,
  HomeWidgetType,
} from '../../settings/validation/HomeWidget';
import { AppointmentMaskOverlay } from '../calendar/components/AppointmentMaskOverlay';
import {
  BLANK_LAYOUT,
  LAYOUT_SIZES,
  WIDGET_COLS,
} from '../files/detail/components/card-widgets/WidgetGroupContent';
import {
  getStartForNewProjectTime,
  useProjectHoursState,
} from '../time-tracker/ProjectHours';
import {
  ProjectTimeMask,
  ProjectTimeMaskProps,
} from '../time-tracker/ProjectTimeMask';
import TimeTemplatesWidget from '../time-tracker/TimeTemplatesWidget';
import { TimeTracker } from '../time-tracker/TimeTracker';
import { useTimeTracker } from '../time-tracker/use-time-tracker';
import { VacationMaskOverlay } from '../vacations/VacationMaskOverlay';

import { CalendarCard } from './components/cards/calendar/CalendarCard';
import { OVERVIEW_ROW_HEIGHT_REM } from './components/cards/overview/components/overview-item/OverviewItem';
import { OVERVIEW_ROW_HEADER_HEIGHT_REM } from './components/cards/overview/components/overview-section/OverviewSection';
import {
  OVERVIEW_CARD_HEADER_HEIGHT_REM,
  OverviewCard,
} from './components/cards/overview/OverviewCard';
import { RelevantCard } from './components/cards/relevant/RelevantCard';
import { EntityWidget } from './components/home-widgets/EntityWidget';
import { HomeWidgetSettings } from './components/home-widgets/home-widget-settings/HomeWidgetSettings';
import { HomeWidgetsContext } from './components/home-widgets/HomeWidgetsContextProvider';
import { DEFAULT_HOME_WIDGET_LAYOUTS } from './components/home-widgets/layouts';
import { WidgetDivider } from './components/home-widgets/WidgetDivider';
import { HomeWidgetGroup } from './components/home-widgets/WidgetGroup';

type WidgetID =
  | 'vacation'
  | 'calendar'
  | 'workHours'
  | 'projectHours'
  | 'relevant'
  | 'overview';

type WidgetsState = Record<WidgetID, boolean>;

const DEFAULT_WIDGETS_STATE_DESKTOP: WidgetsState = {
  vacation: false,
  calendar: false,
  workHours: false,
  projectHours: false,
  relevant: false,
  overview: false,
};

const DEFAULT_WIDGETS_STATE_MOBILE: WidgetsState = {
  vacation: true,
  calendar: true,
  workHours: false,
  projectHours: false,
  relevant: false,
  overview: false,
};

export const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const { activeBackground } = useCustomBackgroundsContext();
  const user = useUser();
  const { rights } = useModuleRights();

  const isDesktop = useMediaQuery<Theme>(
    (theme) => theme.breakpoints.up('xl'),
    {
      noSsr: true,
    }
  );

  const isTablet = useMediaQuery<Theme>(
    (theme) => theme.breakpoints.down('md'),
    {
      noSsr: true,
    }
  );

  const { value: widgetsState, set: setWidgetsState } = useSetting({
    name: `home.widgets.${isDesktop ? 'desktop' : 'mobile'}`,
    scope: 'user',
    defaultValue: isDesktop
      ? DEFAULT_WIDGETS_STATE_DESKTOP
      : DEFAULT_WIDGETS_STATE_MOBILE,
  });

  const isWidgetCollapsed = useCallback(
    (widgetId: string) => {
      return widgetsState[widgetId] ?? false;
    },
    [widgetsState]
  );

  const setWidgetCollapsed = useCallback(
    (widgetId: string) => (collapsed: boolean) => {
      setWidgetsState({ ...widgetsState, [widgetId]: collapsed });
    },
    [setWidgetsState, widgetsState]
  );

  const [editHomeWidget, setEditHomeWidget] = useState<HomeWidget>(null);

  const projectHoursState = useProjectHoursState({ userToTrack: user });

  const canViewProjectTimeTracking = useCanView(AppParts.PROJECTTIMETRACKING);
  const canViewWorkTimeTracking = useCanView(AppParts.WORKTIMETRACKING);
  const canViewCalendar = useCanView(AppParts.CALENDAR);
  const canViewOverviewWidget = useCanView(AppParts.OVERVIEW_WIDGET);

  const [showProjectTimeMask, setShowProjectTimeMask] =
    useState<ProjectTimeMaskProps | null>(null);

  const [showTimeTemplatesMask, setShowTimeTemplatesMask] =
    useState<boolean>(false);

  const [showHomeWidgetSettings, setShowHomeWidgetSettings] =
    useState<boolean>(false);

  const [showVacationMask, setShowVacationMask] = useState<{
    open: boolean;
    date?: Date | null;
    user?: User | null;
    init?: CalendarEventClickData;
  }>({ open: false });

  const tracker = useTimeTracker({
    disabled: !canViewProjectTimeTracking,
    amplitudeEntryPoint: 'HomeWidget',
  });

  const [showAppointmentMask, setShowAppointmentMask] = useState<{
    open: boolean;
    appointmentId?: number | null;
    presetFields?: string | null;
  }>({ open: false });

  const { hideSuggestion } = useSuggestionsContext();
  const {
    layouts,
    breakpoint,
    updateLayout,
    setBreakpoint,
    widgets,
    modifyConf,
    draggedWidget,
    hoveredWidget,
    setDraggedWidgetId,
    setHoveredWidgetId,
  } = useContext(HomeWidgetsContext);

  const heightCorrectedLayouts = useMemo(() => {
    const result = cloneDeep(layouts || BLANK_LAYOUT);
    if (result)
      for (const size of LAYOUT_SIZES) {
        result[size] = result[size].map((lay) => {
          const widget = widgets.find((x) => x.id === lay.i);
          const resolvedHeight = isWidgetCollapsed(widget?.id)
            ? 2
            : !isWidgetCollapsed(widget?.id) && lay.h === 1
            ? lay.minH || 3
            : lay.h;
          return {
            ...lay,
            h: resolvedHeight,
            isResizable: !isWidgetCollapsed(widget?.id),
          } as Layout;
        });
      }

    return result;
  }, [isWidgetCollapsed, layouts, widgets]);

  const handleMerge = useCallback(() => {
    if (draggedWidget.type === HomeWidgetType.widgetGroup) {
      if (hoveredWidget.type === HomeWidgetType.widgetGroup) {
        const newWidgets = cloneDeep(widgets);
        newWidgets
          .find((x) => x.id === hoveredWidget.id)
          ?.subWidgetIds?.push(...draggedWidget.subWidgetIds);

        modifyConf({
          widgets: {
            set: newWidgets,
          },
          layout: {
            remove: [draggedWidget.id],
          },
        });
      }
    }

    if (draggedWidget.type === HomeWidgetType.entityWidget) {
      if (hoveredWidget.type === HomeWidgetType.entityWidget) {
        const newWidget: HomeWidget = {
          id: new Date().toISOString(),
          type: HomeWidgetType.widgetGroup,
          title: t('COMMON.GROUP'),
          subWidgetIds: [draggedWidget?.id, hoveredWidget?.id],
        };
        modifyConf({
          layout: {
            remove: [draggedWidget?.id, hoveredWidget?.id],
            add: [newWidget?.id],
          },
          widgets: {
            add: [newWidget],
          },
        });
      }

      if (hoveredWidget.type === HomeWidgetType.widgetGroup) {
        const newWidgets = cloneDeep(widgets);
        newWidgets
          .find((x) => x.id === hoveredWidget.id)
          ?.subWidgetIds?.push(draggedWidget.id);

        modifyConf({
          widgets: {
            set: newWidgets,
          },
          layout: {
            remove: [draggedWidget.id],
          },
        });
      }
    }
  }, [
    draggedWidget?.id,
    draggedWidget?.subWidgetIds,
    draggedWidget?.type,
    hoveredWidget?.id,
    hoveredWidget?.type,
    modifyConf,
    t,
    widgets,
  ]);

  const homeWidgetSettingsView = useMemo(
    () => ({
      view: (
        <HomeWidgetSettings
          // There is a weird issue with NavigationOverlay that causes the
          // HomeWidgetSettings to render with the old widgetId initially.
          // Because of this, it initializes its state incorrectly with a
          // different (previous) widget. Use a key to force re-mount with
          // up-to-date values.
          key={editHomeWidget?.id}
          amplitudeEntryPoint="HomeWidget"
          widgetId={editHomeWidget?.id}
        />
      ),
      title: t('TIME_TRACKER.PROJECT_TIME'),
    }),
    [editHomeWidget?.id, t]
  );

  const [minOverviewCardHeight, setMinOverviewCardHeight] = useState(
    heightCorrectedLayouts[breakpoint].find(
      (layout) => layout.i === HomeWidgetType.overviewWidget
    ).h
  );
  const [minRelevantCardHeight, setMinRelevantCardHeight] = useState(
    heightCorrectedLayouts[breakpoint].find(
      (layout) => layout.i === HomeWidgetType.relevantWidget
    ).h
  );
  const minEntityCardHeight = 6;

  const handleOverviewCardActiveItems = useCallback(
    (rows: number, headerRows: number) => {
      // GridLayout rowHeight has DEFAULT_GRID_ROW_HEIGHT_REM = 2
      // OverviewCard:
      // - row has height OVERVIEW_ROW_HEIGHT_REM = 2.5
      // - header row has height OVERVIEW_ROW_HEADER_HEIGHT_REM = 3.5
      // - header has height OVERVIEW_CARD_HEADER_HEIGHT_REM = 4
      // and we have to add widget top padding which is DEFAULT_GRID_GAP_REM = 1
      const rowHeight = OVERVIEW_ROW_HEIGHT_REM / DEFAULT_GRID_ROW_HEIGHT_REM;
      const headerRowHeight =
        OVERVIEW_ROW_HEADER_HEIGHT_REM / DEFAULT_GRID_ROW_HEIGHT_REM;
      const headerHeight =
        OVERVIEW_CARD_HEADER_HEIGHT_REM / DEFAULT_GRID_ROW_HEIGHT_REM;
      const gapHeight = DEFAULT_GRID_GAP_REM / DEFAULT_GRID_ROW_HEIGHT_REM;

      setMinOverviewCardHeight(
        Math.max(
          Math.ceil(
            rows * rowHeight +
              headerRows * headerRowHeight +
              headerHeight +
              gapHeight
          ),
          DEFAULT_HOME_WIDGET_LAYOUTS[breakpoint].find(
            (layout) => layout.i === HomeWidgetType.overviewWidget
          ).minH
        )
      );
    },
    [breakpoint]
  );

  const handleRelevantCardActiveItems = useCallback(
    (rows: number) => {
      // GridLayout rowHeight has DEFAULT_GRID_ROW_HEIGHT_REM = 2
      // RelevantCard:
      // - row has height DEFAULT_GRID_ROW_HEIGHT_REM = 2
      // - header has height DEFAULT_CARD_HEADER_HEIGHT_REM = 3
      // and we have to add widget top padding which is DEFAULT_GRID_GAP_REM = 1
      const rowHeight = 1;
      const headerHeight =
        DEFAULT_CARD_HEADER_HEIGHT_REM / DEFAULT_GRID_ROW_HEIGHT_REM;
      const gapHeight = DEFAULT_GRID_GAP_REM / DEFAULT_GRID_ROW_HEIGHT_REM;

      setMinRelevantCardHeight(
        Math.max(
          Math.ceil(rows * rowHeight + headerHeight + gapHeight),
          DEFAULT_HOME_WIDGET_LAYOUTS[breakpoint].find(
            (layout) => layout.i === HomeWidgetType.relevantWidget
          ).minH
        )
      );
    },
    [breakpoint]
  );

  const homePageLayouts = useMemo(
    () => ({
      ...heightCorrectedLayouts,
      [breakpoint]: heightCorrectedLayouts[breakpoint].map((layout) => {
        if (isWidgetCollapsed(layout.i)) {
          return layout;
        }
        if (layout.i === HomeWidgetType.overviewWidget) {
          return {
            ...layout,
            h: Math.max(minOverviewCardHeight, layout.h),
            minH: minOverviewCardHeight,
          };
        }
        if (layout.i === HomeWidgetType.relevantWidget) {
          return {
            ...layout,
            h: Math.max(minRelevantCardHeight, layout.h),
            minH: minRelevantCardHeight,
          };
        }
        // entity widget layout has 'i' value set as date
        if (DateTime.fromISO(layout.i).isValid) {
          return {
            ...layout,
            h: Math.max(layout.h, minEntityCardHeight),
            minH: minEntityCardHeight,
          };
        }
        return layout;
      }),
    }),
    [
      breakpoint,
      heightCorrectedLayouts,
      isWidgetCollapsed,
      minOverviewCardHeight,
      minRelevantCardHeight,
    ]
  );

  return (
    <div data-test-id="home-page-wrapper">
      <NavigationOverlay
        classes={{
          wrapper: styles.dialogWrapper,
          paper: styles.paper,
        }}
        open={showHomeWidgetSettings}
        initialView={homeWidgetSettingsView}
        close={() => {
          setShowHomeWidgetSettings(false);
        }}
      />
      <NavigationOverlay
        classes={{
          wrapper: styles.dialogWrapper,
          paper: styles.paper,
        }}
        open={showProjectTimeMask !== null}
        initialView={{
          view: (
            <ProjectTimeMask
              user={user}
              item={{ ...showProjectTimeMask?.item, insertTime: undefined }}
              init={showProjectTimeMask?.init}
              amplitudeEntryPoint="HomeWidget"
              onCompleted={() => {
                const id = showProjectTimeMask?.init?.id;
                if (id) hideSuggestion(id);
              }}
              createdFrom={showProjectTimeMask?.createdFrom}
            />
          ),
          title: t('TIME_TRACKER.PROJECT_TIME'),
        }}
        close={() => {
          setShowProjectTimeMask(null);
        }}
      />
      <NavigationOverlay
        classes={{
          wrapper: styles.dialogWrapper,
          paper: styles.paper,
        }}
        open={showTimeTemplatesMask}
        initialView={{
          view: (
            <TimeTemplatesWidget
              mobileMode={true}
              onTemplateSelect={(init, createdFrom) => {
                const { events, date } = projectHoursState;
                const adjustedDate = getStartForNewProjectTime(
                  date.from,
                  events,
                  tracker
                );

                setShowProjectTimeMask({
                  init: { date: adjustedDate, ...init },
                  createdFrom,
                });
                setShowTimeTemplatesMask(false);
              }}
            />
          ),
          title: 'Templates',
        }}
        close={() => {
          setShowTimeTemplatesMask(false);
        }}
      />
      <VacationMaskOverlay
        open={showVacationMask.open}
        date={showVacationMask.date}
        user={showVacationMask.user}
        init={showVacationMask.init}
        onClose={() => setShowVacationMask({ open: false })}
      />
      <AppointmentMaskOverlay
        open={showAppointmentMask.open}
        appointmentId={showAppointmentMask.appointmentId}
        presetFields={showAppointmentMask.presetFields}
        onClose={() => setShowAppointmentMask({ open: false })}
      />
      <FixedBackground
        className={styles.backgroundShape}
        src={activeBackground?.url}
        blend="auto"
      />
      {!isTablet ? (
        <GridLayout
          layouts={homePageLayouts}
          onBreakpointChange={setBreakpoint}
          draggableCancel="a"
          onDragStart={(e, layout) => {
            setDraggedWidgetId(layout.i);
          }}
          onDragStop={(layout) => {
            if (draggedWidget && hoveredWidget) {
              handleMerge();
            } else {
              updateLayout(layout);
            }
            setDraggedWidgetId(null);
            setHoveredWidgetId(null);
          }}
          onResizeStop={(layout) => {
            updateLayout(layout);
          }}
          cols={WIDGET_COLS}
        >
          {homePageLayouts?.[breakpoint]?.map((w: Layout) => {
            const widget = widgets.find((x) => w.i === x.id);
            let comp = null;
            switch (widget?.type) {
              case HomeWidgetType.calendarWidget:
                if (!canViewCalendar) break;
                comp = (
                  <CalendarCard
                    sx={{ height: '100%' }}
                    forceMobileView={true}
                    collapsed={isWidgetCollapsed(widget.id)}
                    onCollapsedChange={setWidgetCollapsed(widget.id)}
                    onOpenAppointmentMask={(options) => {
                      setShowAppointmentMask({ open: true, ...options });
                    }}
                  />
                );
                break;
              case HomeWidgetType.overviewWidget:
                if (!canViewOverviewWidget) break;
                comp = (
                  <OverviewCard
                    sx={{
                      height: !isWidgetCollapsed(widget.id) ? '100%' : '100%',
                    }}
                    collapsed={isWidgetCollapsed(widget.id)}
                    onCollapsedChange={setWidgetCollapsed(widget.id)}
                    onActiveRowsChange={handleOverviewCardActiveItems}
                  />
                );
                break;
              case HomeWidgetType.relevantWidget:
                comp = (
                  <RelevantCard
                    sx={{
                      height: !isWidgetCollapsed(widget.id) ? '100%' : '100%',
                    }}
                    collapsed={isWidgetCollapsed(widget.id)}
                    onCollapsedChange={setWidgetCollapsed(widget.id)}
                    onActiveRowsChange={handleRelevantCardActiveItems}
                  />
                );
                break;
              case HomeWidgetType.timeTracker:
                comp = <TimeTracker className={styles.timeTracker} />;
                break;
              case HomeWidgetType.entityWidget:
                comp = (
                  <EntityWidget
                    {...widget}
                    onDeleteClick={() =>
                      modifyConf({ widgets: { remove: [widget.id] } })
                    }
                    onEditClick={() => {
                      setEditHomeWidget(widget);
                      setShowHomeWidgetSettings(true);
                    }}
                    pageSize={DEFAULT_WIDGET_QUERY_SIZE}
                    collapsed={isWidgetCollapsed(widget.id)}
                    onCollapsedChange={setWidgetCollapsed(widget.id)}
                    collapsible={true}
                  />
                );
                break;
              case HomeWidgetType.widgetGroup:
                comp = (
                  <HomeWidgetGroup
                    {...widget}
                    subWidgets={widget.subWidgetIds.map((id) =>
                      widgets.find((w) => w.id === id)
                    )}
                    onDeleteClick={() =>
                      modifyConf({ widgets: { remove: [widget.id] } })
                    }
                    onEditClick={() => {
                      setEditHomeWidget(widget);
                      setShowHomeWidgetSettings(true);
                    }}
                    onBreakupClick={() => {
                      modifyConf({
                        widgets: { remove: [widget.id] },
                        layout: {
                          remove: [widget.id],
                          add: widget.subWidgetIds,
                        },
                      });
                    }}
                  />
                );
                break;
              case HomeWidgetType.divider:
                comp = (
                  <WidgetDivider
                    {...widget}
                    onDeleteClick={() =>
                      modifyConf({ widgets: { remove: [widget.id] } })
                    }
                    onEditClick={() => {
                      setShowHomeWidgetSettings(true);
                      setEditHomeWidget(widget);
                    }}
                  />
                );

                break;
              default:
                break;
            }

            return (
              <div
                key={widget?.id}
                className={clsx(styles.widgetWrap, {
                  [styles.hoveredWidget]:
                    draggedWidget &&
                    draggedWidget.type === HomeWidgetType.entityWidget &&
                    widget.type === HomeWidgetType.entityWidget &&
                    hoveredWidget?.id === widget?.id,
                  [styles.draggedWidget]: draggedWidget?.id === widget?.id,
                })}
              >
                <div style={{ height: '100%' }}>{comp}</div>
              </div>
            );
          })}
        </GridLayout>
      ) : (
        <YSplit className={styles.outerWrap}>
          <Stack
            direction={!isTablet ? 'row' : 'column'}
            height="auto"
            gap="1rem"
            flex="1"
            flexBasis="0"
            maxWidth="100%"
            width="85rem"
            padding={
              isDesktop
                ? '0 1rem 1rem 1rem'
                : isTablet
                ? '1rem 0.75rem 1.5rem 0.75rem'
                : '0 0.75rem 1.5rem 0.75rem'
            }
          >
            <Grid container spacing="1rem">
              {!isDesktop && canViewWorkTimeTracking && (
                <Grid item xs={6}>
                  <TimeTracker className={styles.timeTracker} />
                </Grid>
              )}

              <Grid item xs={!isDesktop && canViewWorkTimeTracking ? 6 : 12}>
                <Box sx={{ alignSelf: 'center' }}>
                  <UserButtonMobile />
                </Box>
              </Grid>
            </Grid>

            <RelevantCard
              collapsed={isWidgetCollapsed('relevant')}
              onCollapsedChange={setWidgetCollapsed('relevant')}
            />
            {!isTimeTrackingUser(rights) && canViewCalendar && (
              <CalendarCard
                height="fixed"
                forceMobileView={true}
                collapsed={isWidgetCollapsed('calendar')}
                onCollapsedChange={setWidgetCollapsed('calendar')}
                onOpenAppointmentMask={(options) => {
                  setShowAppointmentMask({ open: true, ...options });
                }}
              />
            )}
            {!isTimeTrackingUser(rights) && canViewOverviewWidget && (
              <OverviewCard
                collapsed={isWidgetCollapsed('overview')}
                onCollapsedChange={setWidgetCollapsed('overview')}
              />
            )}
          </Stack>
        </YSplit>
      )}
    </div>
  );
};
